import React, { useState } from 'react';
import axios from 'axios';
import {
  FiArrowLeft,
  FiPackage,
  FiMapPin,
  FiChevronRight,
} from 'react-icons/fi';
import { getImagenUrl } from 'shared/api';
import { fmtMonto } from 'shared/format';
import ClientePedidoDetalle from './ClientePedidoDetalle';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ESTADO_IDX = [
  'pendiente', 'en_revision', 'aprobado', 'esperando_pago', 'pago_en_verificacion',
  'pagado', 'armado_cocina', 'armado', 'listo', 'despachado', 'en_camino', 'entregado',
];

const ESTADO_LABEL = {
  pendiente: 'Pendiente', en_revision: 'En Revisión', aprobado: 'Método de Pago',
  esperando_pago: 'Factura', pago_en_verificacion: 'Verificando', pagado: 'Factura',
  armado_cocina: 'Armando', armado: 'Armando', listo: 'Listo', despachado: 'Despachado',
  en_camino: 'En Camino', llegada: 'Llegada', entregado: 'Entregado',
  rechazado: 'Rechazado', cancelado: 'Cancelado', credito_pagado: 'Crédito pagado',
};

const ESTADO_COLOR = {
  pendiente: '#FF9500', en_revision: '#FF9500', aprobado: '#0d904f',
  esperando_pago: '#FF9500', pago_en_verificacion: '#FF9500', pagado: '#0d904f',
  armado_cocina: '#ff6d00', armado: '#ff6d00', listo: '#0d904f', despachado: '#00bcd4',
  en_camino: '#00bcd4', llegada: '#009688', entregado: '#0d904f',
  rechazado: '#ea4335', cancelado: '#9aa0a6', credito_pagado: '#0d904f',
};

function estadoDominante(pedidos) {
  let idx = -1;
  (pedidos || []).forEach((p) => {
    const i = ESTADO_IDX.indexOf(p.estado);
    if (i > idx) idx = i;
  });
  if (idx >= 0) return ESTADO_IDX[idx];
  return 'pendiente';
}

export default function ClientePedidoCorporativo({
  config,
  sessionId,
  schemaName,
  pedidos,
  onBack,
  onConfirmado,
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [error, setError] = useState('');
  const [pedidoSelId, setPedidoSelId] = useState(null);

  if (!pedidos || pedidos.length === 0) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
        No hay pedidos corporativos activos.
      </div>
    );
  }

  // Detalle de una sucursal especifica (fases completas del pedido).
  if (pedidoSelId) {
    return (
      <ClientePedidoDetalle
        pedidoId={pedidoSelId}
        inline={true}
        config={config}
        sessionId={sessionId}
        onBack={() => setPedidoSelId(null)}
        onGoToChat={() => {}}
        onGoToCatalog={() => setPedidoSelId(null)}
        pedidosActivos={pedidos}
        pedidoSeleccionadoId={pedidoSelId}
        onSeleccionarPedido={(id) => id && setPedidoSelId(id)}
      />
    );
  }

  const grupoCobro = pedidos[0].grupo_cobro;
  const totalGeneral = pedidos.reduce((s, p) => s + (parseFloat(p.total) || 0), 0);
  const dominante = estadoDominante(pedidos);
  const hayPendientes = pedidos.some((p) => p.estado === 'pendiente');

  const productosDe = (p) => {
    if (Array.isArray(p.productos)) return p.productos;
    if (typeof p.productos === 'string') { try { return JSON.parse(p.productos); } catch { return []; } }
    return [];
  };

  const confirmar = async () => {
    setConfirmando(true);
    setError('');
    try {
      const res = await axios.put(`${API_URL}/pedidos/corporativo/${grupoCobro}/confirmar`, {
        schema_name: schemaName,
        tipo_entrega: 'encomienda',
        session_id: sessionId || null,
      });
      if (res.data.status === 'success') {
        if (onConfirmado) onConfirmado();
      } else {
        setError(res.data.message || 'No se pudo confirmar');
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Error de conexión al confirmar');
    } finally {
      setConfirmando(false);
    }
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', width: '100%', padding: '8px 4px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 4px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', padding: 4 }}>
          <FiArrowLeft size={22} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-primary)' }}>
            Pedido corporativo
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            {pedidos.length} sucursal{pedidos.length !== 1 ? 'es' : ''} · Total{' '}
            <strong style={{ color: 'var(--color-secondary)' }}>${fmtMonto(totalGeneral)}</strong>
          </div>
        </div>
        <span style={{
          padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
          background: ESTADO_COLOR[dominante] + '20', color: ESTADO_COLOR[dominante],
        }}>
          {ESTADO_LABEL[dominante] || dominante}
        </span>
      </div>

      {/* Confirmación unificada */}
      {hayPendientes && (
        <div style={{
          margin: '12px 0', padding: 14, borderRadius: 14, border: '1px solid var(--color-border)',
          background: 'var(--color-surface)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-primary)', marginBottom: 8 }}>
            Confirmar pedido
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '8px 12px',
            borderRadius: 10, background: 'var(--color-bg)', fontSize: 12, color: 'var(--color-text-secondary)',
          }}>
            <FiMapPin size={14} color="var(--color-secondary)" />
            Entrega por <strong style={{ color: 'var(--color-text)' }}>Encomienda</strong>
          </div>
          {error && <div style={{ fontSize: 12, color: 'var(--color-danger)', marginBottom: 8 }}>{error}</div>}
          <button
            onClick={confirmar}
            disabled={confirmando}
            style={{
              width: '100%', padding: '12px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
              color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
              opacity: confirmando ? 0.7 : 1,
            }}
          >
            {confirmando ? 'Confirmando...' : `Confirmar ${pedidos.length} pedido${pedidos.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      {/* Estado por sucursal */}
      {pedidos.map((p) => {
        const prods = productosDe(p);
        const label = p.sucursal_nombre || `Sucursal ${p.sucursal_id || ''}`;
        return (
          <div key={p.id} style={{
            margin: '12px 0', borderRadius: 14, border: '1px solid var(--color-border)',
            background: 'var(--color-surface)', overflow: 'hidden',
          }}>
            <button
              onClick={() => setPedidoSelId(p.id)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                border: 'none', borderBottom: '1px solid var(--color-border)',
                background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
              }}
            >
              <FiMapPin size={16} color="var(--color-secondary)" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-primary)' }}>{label}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                  Pedido #{p.id} · {p.direccion_entrega || 'Sin dirección'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{
                  display: 'inline-block', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                  background: (ESTADO_COLOR[p.estado] || '#9aa0a6') + '20', color: ESTADO_COLOR[p.estado] || '#9aa0a6',
                }}>
                  {ESTADO_LABEL[p.estado] || p.estado}
                </span>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-secondary)', marginTop: 4 }}>
                  ${fmtMonto(p.total)}
                </div>
              </div>
              <FiChevronRight size={16} color="var(--color-text-muted)" />
            </button>
            <div style={{ padding: '10px 14px' }}>
              {prods.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Sin productos</div>
              ) : (
                prods.map((prod, i) => {
                  const cant = prod.pesable ? `${(prod.cantidad / 1000).toFixed(2)} kg` : `${prod.cantidad} und`;
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0',
                      borderBottom: i < prods.length - 1 ? '1px dashed var(--color-border)' : 'none',
                    }}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {getImagenUrl(prod.imagen_url) ? (
                          <img src={getImagenUrl(prod.imagen_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <FiPackage size={15} color="var(--color-text-muted)" />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{prod.nombre}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{cant}</div>
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text)' }}>
                        ${fmtMonto(prod.subtotal || prod.precio)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}

      {!hayPendientes && (
        <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 13 }}>
          Estos pedidos ya no están pendientes de confirmación.
        </div>
      )}
    </div>
  );
}
